import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { theme } from './theme';

import { PublicLayout } from './components/layout/PublicLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { AppLayout } from './components/layout/AppLayout';

const LandingPage        = React.lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const PricingPage        = React.lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const OfertaPage         = React.lazy(() => import('./pages/OfertaPage').then(m => ({ default: m.OfertaPage })));
const PrivacyPolicyPage  = React.lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const DpaPage            = React.lazy(() => import('./pages/DpaPage').then(m => ({ default: m.DpaPage })));
const ApiDocsPage        = React.lazy(() => import('./pages/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })));
const StatusPage         = React.lazy(() => import('./pages/StatusPage').then(m => ({ default: m.StatusPage })));
const DeliverabilityPage = React.lazy(() => import('./pages/DeliverabilityPage').then(m => ({ default: m.DeliverabilityPage })));
const SpfCheckPage       = React.lazy(() => import('./pages/SpfCheckPage').then(m => ({ default: m.SpfCheckPage })));
const DmarcCheckPage     = React.lazy(() => import('./pages/DmarcCheckPage').then(m => ({ default: m.DmarcCheckPage })));
const NotFoundPage       = React.lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

const SpfToolPage        = React.lazy(() => import('./pages/tools/SpfToolPage').then(m => ({ default: m.SpfToolPage })));
const DmarcToolPage      = React.lazy(() => import('./pages/tools/DmarcToolPage').then(m => ({ default: m.DmarcToolPage })));
const BlacklistToolPage  = React.lazy(() => import('./pages/tools/BlacklistToolPage').then(m => ({ default: m.BlacklistToolPage })));
const EmailToolPage      = React.lazy(() => import('./pages/tools/EmailToolPage').then(m => ({ default: m.EmailToolPage })));

const LoginPage          = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage       = React.lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage  = React.lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage    = React.lazy(() => import('./pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));

const DashboardPage      = React.lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const BatchValidatePage  = React.lazy(() => import('./pages/BatchValidatePage').then(m => ({ default: m.BatchValidatePage })));
const HistoryPage        = React.lazy(() => import('./pages/HistoryPage').then(m => ({ default: m.HistoryPage })));
const AccountSettingsPage = React.lazy(() => import('./pages/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })));
const StatsPage          = React.lazy(() => import('./pages/StatsPage').then(m => ({ default: m.StatsPage })));
const WebhooksPage       = React.lazy(() => import('./pages/WebhooksPage').then(m => ({ default: m.WebhooksPage })));
const BillingPage        = React.lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const EmailListsPage     = React.lazy(() => import('./pages/EmailListsPage').then(m => ({ default: m.EmailListsPage })));
const AdminPage          = React.lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));

const PageLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress size={32} sx={{ color: 'primary.main' }} />
  </Box>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('auth_token');
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const pub  = (el: React.ReactElement) => <PublicLayout>{el}</PublicLayout>;
const auth = (el: React.ReactElement) => <AuthLayout>{el}</AuthLayout>;
const app  = (el: React.ReactElement) => <ProtectedRoute><AppLayout>{el}</AppLayout></ProtectedRoute>;

export const App: React.FC = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/"                       element={<LandingPage />} />
          <Route path="/pricing"                element={pub(<PricingPage />)} />
          <Route path="/oferta"                 element={pub(<OfertaPage />)} />
          <Route path="/privacy"                element={pub(<PrivacyPolicyPage />)} />
          <Route path="/dpa"                    element={pub(<DpaPage />)} />
          <Route path="/api-docs"               element={pub(<ApiDocsPage />)} />
          <Route path="/status"                 element={pub(<StatusPage />)} />
          <Route path="/deliverability"         element={pub(<DeliverabilityPage />)} />
          <Route path="/spf-check"              element={pub(<SpfCheckPage />)} />
          <Route path="/dmarc-check"            element={pub(<DmarcCheckPage />)} />
          <Route path="/tools/spf-check"        element={pub(<SpfToolPage />)} />
          <Route path="/tools/dmarc-check"      element={pub(<DmarcToolPage />)} />
          <Route path="/tools/blacklist-check"  element={pub(<BlacklistToolPage />)} />
          <Route path="/tools/email-validate"   element={pub(<EmailToolPage />)} />

          <Route path="/login"           element={auth(<LoginPage />)} />
          <Route path="/register"        element={auth(<RegisterPage />)} />
          <Route path="/forgot-password" element={auth(<ForgotPasswordPage />)} />
          <Route path="/reset-password"  element={auth(<ResetPasswordPage />)} />
          <Route path="/verify-email"    element={auth(<VerifyEmailPage />)} />

          <Route path="/dashboard"       element={app(<DashboardPage />)} />
          <Route path="/validate-batch"  element={app(<BatchValidatePage />)} />
          <Route path="/history"         element={app(<HistoryPage />)} />
          <Route path="/settings"        element={app(<AccountSettingsPage />)} />
          <Route path="/stats"           element={app(<StatsPage />)} />
          <Route path="/webhooks"        element={app(<WebhooksPage />)} />
          <Route path="/billing"         element={app(<BillingPage />)} />
          <Route path="/email-lists"     element={app(<EmailListsPage />)} />
          <Route path="/admin"           element={<ProtectedRoute><AppLayout isAdmin><AdminPage /></AppLayout></ProtectedRoute>} />

          <Route path="*" element={pub(<NotFoundPage />)} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </ThemeProvider>
);
